/**
 * Custom hook for fetching bookmaker and fancy data
 * Uses React Query with automatic refetching for live updates
 */

import { useQuery } from '@tanstack/react-query';
import { API_URL } from '@/utils/constants';

/**
 * Helper function to filter fancy data
 */
const toNum = (v) => {
  if (v === null || v === undefined) return NaN;
  const n = Number(String(v).trim());
  return Number.isFinite(n) ? n : NaN;
};

const filterFancy = (rows = []) =>
  rows
    .filter((r) => String(r.statusName).toUpperCase() === 'ACTIVE')
    .filter((r) => {
      const yes = toNum(r.oddsYes);
      const no = toNum(r.oddsNo);
      return yes >= 90 || no >= 90;
    });

/**
 * Fetch bookmaker and fancy data for a match
 * @param {string} eventId - Event ID
 * @returns {Object} React Query result with bookmaker and fancy data
 */
export const useBookmakerFancy = (eventId) => {
  return useQuery({
    queryKey: ['bookmakerFancy', eventId],
    queryFn: async () => {
      if (!eventId) return { bookMaker: [], fancy: [] };

      const response = await fetch(
        `${API_URL}/sports/bookmakerFancy?eventid=${eventId}`,
        {
          headers: { 'Content-Type': 'application/json' },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch bookmaker/fancy data');
      }

      const data = await response.json();

      return {
        bookMaker: data.bookMaker || [],
        fancy: filterFancy(data.fancy || []),
      };
    },
    enabled: !!eventId,
    staleTime: 3 * 1000, // 3 seconds
    refetchInterval: 5 * 1000, // Refetch every 5 seconds
    refetchIntervalInBackground: true,
    retry: 2,
    retryDelay: 1000,
  });
};
